import { useEffect, useState } from "react";
import "./RecipeZone.css";

const RecipeZone = ({ onBack, initialLanguage = "zh", onLanguageChange }) => {
  const [activeTab, setActiveTab] = useState("ingredients");
  const [language, setLanguage] = useState(initialLanguage);

  // Update language when initialLanguage prop changes
  useEffect(() => {
    setLanguage(initialLanguage);
  }, [initialLanguage]);

  // Handle language toggle with URL update
  const handleLanguageToggle = () => {
    const newLang = language === "zh" ? "en" : "zh";
    setLanguage(newLang);
    if (onLanguageChange) {
      onLanguageChange(newLang);
    }
  };

  const recipes = {
    zh: {
      title: "炸雞肉丸",
      subtitle: "Fried Chicken Meatballs",
      description: "外酥內嫩，口感Q彈，適合作為小吃或配菜的經典炸雞肉丸！",
      tabLabels: {
        ingredients: "材料",
        instructions: "步驟",
        tips: "小撇步",
      },
      sectionTitles: {
        ingredients: "材料清單",
        instructions: "製作步驟",
        tips: "烹飪小撇步",
        serving: "享用建議",
      },
      ingredients: [
        { name: "雞胸肉或雞腿肉", amount: "300-500克", note: "腿肉更嫩更多汁" },
        { name: "雞蛋", amount: "1個", note: "或僅蛋清" },
        { name: "澱粉", amount: "2-3湯匙", note: "玉米澱粉或馬鈴薯澱粉" },
        { name: "蔥薑末", amount: "適量", note: "" },
        { name: "鹽、白胡椒、料酒", amount: "適量", note: "" },
        {
          name: "可選增香料",
          amount: "",
          note: "洋蔥末、胡蘿蔔末、香菇末或起司絲",
        },
      ],
      instructions: [
        {
          step: 1,
          title: "準備雞肉",
          details: [
            "去除雞胸肉的筋膜和脂肪，用刀背輕輕拍打使肉質鬆散",
            "手工剁碎或用食物處理機打成泥狀至順滑",
            "加入蔥薑末、鹽、白胡椒、料酒和蛋清，朝一個方向攪拌至黏稠有彈性（上勁）",
          ],
        },
        {
          step: 2,
          title: "增強口感",
          details: [
            "分次加入澱粉混合均勻，可加1-2湯匙水或牛奶增加嫩度",
            "若想更Q彈：可加入少量起司絲或魚漿",
          ],
        },
        {
          step: 3,
          title: "塑形與裹粉",
          details: [
            "手沾少許水，取適量肉餡捏成乒乓球大小的丸子",
            "若要更酥脆：依序裹上麵粉→蛋液→麵包粉（三層裹粉法）",
          ],
        },
        {
          step: 4,
          title: "油炸",
          details: [
            "油溫加熱至150-160°C（筷子插入會冒小氣泡）",
            "中小火分批炸5-7分鐘至金黃色",
            "可選：提高油溫復炸10秒使外皮更酥脆",
          ],
        },
      ],
      tips: [
        "防止散開：確保肉餡充分攪拌上勁後再塑形",
        "避免油溫過高：以免外焦內生",
        "健康替代：可用180°C氣炸鍋炸12-15分鐘，中途翻面",
      ],
      serving:
        "趁熱搭配番茄醬、甜辣醬或照燒醬享用。若想更健康，也可選擇蒸或烤的方式。",
      footer: "🍳 美味食譜分享 - 讓烹飪變得簡單有趣！",
      backBtn: "返回",
      recipeTitle: "食譜區",
      stepLabel: "步驟",
    },
    en: {
      title: "Fried Chicken Meatballs",
      subtitle: "炸雞肉丸",
      description:
        "Crispy on the outside, tender on the inside, and pleasantly bouncy—a popular snack or side dish!",
      tabLabels: {
        ingredients: "Ingredients",
        instructions: "Instructions",
        tips: "Tips",
      },
      sectionTitles: {
        ingredients: "Ingredients List",
        instructions: "Cooking Steps",
        tips: "Cooking Tips",
        serving: "Serving Suggestions",
      },
      ingredients: [
        {
          name: "Chicken breast or thigh meat",
          amount: "300-500g",
          note: "Thigh meat yields juicier results",
        },
        { name: "Egg", amount: "1", note: "Or egg white only" },
        {
          name: "Starch",
          amount: "2-3 tbsp",
          note: "Cornstarch or potato starch",
        },
        {
          name: "Finely chopped scallions and ginger",
          amount: "To taste",
          note: "",
        },
        {
          name: "Salt, white pepper, cooking wine",
          amount: "To taste",
          note: "Shaoxing wine",
        },
        {
          name: "Optional flavor boosters",
          amount: "",
          note: "Minced onion, carrot, shiitake mushroom, or cheese shreds",
        },
      ],
      instructions: [
        {
          step: 1,
          title: "Prepare the Chicken",
          details: [
            "Trim sinew and fat from chicken breast. Pound lightly with the back of a knife to loosen fibers",
            "Finely mince by hand or process in a food processor until smooth",
            "Add scallions, ginger, salt, white pepper, cooking wine, and egg white. Stir vigorously in one direction until sticky and elastic",
          ],
        },
        {
          step: 2,
          title: "Enhance Texture",
          details: [
            "Mix in starch gradually. For extra tenderness, add 1-2 tablespoons of water or milk while stirring",
            "For a bouncier texture: add a small amount of cheese shreds or fish paste",
          ],
        },
        {
          step: 3,
          title: "Shape and Coat",
          details: [
            "Wet your hands lightly with water. Scoop portions of the mixture and shape into balls (about ping-pong ball size)",
            "For extra crispiness: coat each ball in sequence—flour → beaten egg → breadcrumbs (three-stage coating)",
          ],
        },
        {
          step: 4,
          title: "Fry",
          details: [
            "Heat oil to 150-160°C (300-320°F)—small bubbles form around a chopstick inserted in the oil",
            "Fry in batches over medium-low heat for 5-7 minutes until golden brown",
            "Optional: increase oil temperature and refry for 10 seconds to crisp the exterior further",
          ],
        },
      ],
      tips: [
        "To prevent crumbling: ensure the meat mixture is well-bound and elastic before shaping",
        "Avoid excessively high oil temperature to prevent burning the outside while leaving the inside undercooked",
        "Healthier alternative: air fry at 180°C (356°F) for 12-15 minutes, flipping halfway through",
      ],
      serving:
        "Serve hot with ketchup, sweet chili sauce, or teriyaki glaze. For a lighter version, steam or bake the meatballs instead of frying.",
      footer: "🍳 Delicious Recipe Sharing - Making Cooking Simple and Fun!",
      backBtn: "Back",
      recipeTitle: "Recipe Zone",
      stepLabel: "Step",
    },
  };

  const recipe = recipes[language];

  return (
    <div className="recipe-zone">
      {/* Header */}
      <header className="recipe-header">
        <button className="back-btn" onClick={onBack}>
          ← {recipe.backBtn}
        </button>
        <h1 className="recipe-title">
          <span className="title-icon">🍗</span>
          {recipe.recipeTitle}
        </h1>
        <button
          className="language-toggle"
          onClick={handleLanguageToggle}
          title={language === "zh" ? "Switch to English" : "切換至中文"}
        >
          {language === "zh" ? "EN" : "中"}
        </button>
      </header>

      {/* Recipe Card */}
      <div className="recipe-card">
        <div className="recipe-hero">
          <div className="hero-emoji">🍗</div>
          <h2 className="hero-title">{recipe.title}</h2>
          <p className="hero-subtitle">{recipe.subtitle}</p>
          <p className="hero-description">{recipe.description}</p>
        </div>

        {/* Tabs */}
        <div className="recipe-tabs">
          <button
            className={`tab-btn ${activeTab === "ingredients" ? "active" : ""}`}
            onClick={() => setActiveTab("ingredients")}
          >
            <span className="tab-icon">🥩</span>
            {recipe.tabLabels.ingredients}
          </button>
          <button
            className={`tab-btn ${
              activeTab === "instructions" ? "active" : ""
            }`}
            onClick={() => setActiveTab("instructions")}
          >
            <span className="tab-icon">👨‍🍳</span>
            {recipe.tabLabels.instructions}
          </button>
          <button
            className={`tab-btn ${activeTab === "tips" ? "active" : ""}`}
            onClick={() => setActiveTab("tips")}
          >
            <span className="tab-icon">💡</span>
            {recipe.tabLabels.tips}
          </button>
        </div>

        {/* Tab Content */}
        <div className="recipe-content">
          {activeTab === "ingredients" && (
            <div className="ingredients-section">
              <h3 className="section-title">
                <span className="title-icon">🥩</span>
                {recipe.sectionTitles.ingredients}
              </h3>
              <ul className="ingredients-list">
                {recipe.ingredients.map((item, index) => (
                  <li key={index} className="ingredient-item">
                    <div className="ingredient-main">
                      <span className="ingredient-name">{item.name}</span>
                      {item.amount && (
                        <span className="ingredient-amount">{item.amount}</span>
                      )}
                    </div>
                    {item.note && (
                      <span className="ingredient-note">💭 {item.note}</span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {activeTab === "instructions" && (
            <div className="instructions-section">
              <h3 className="section-title">
                <span className="title-icon">👨‍🍳</span>
                {recipe.sectionTitles.instructions}
              </h3>
              <div className="instructions-list">
                {recipe.instructions.map((instruction) => (
                  <div key={instruction.step} className="instruction-card">
                    <div className="instruction-header">
                      <span className="step-number">
                        {recipe.stepLabel} {instruction.step}
                      </span>
                      <h4 className="step-title">{instruction.title}</h4>
                    </div>
                    <ul className="step-details">
                      {instruction.details.map((detail, idx) => (
                        <li key={idx} className="detail-item">
                          <span className="bullet">•</span>
                          {detail}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === "tips" && (
            <div className="tips-section">
              <h3 className="section-title">
                <span className="title-icon">💡</span>
                {recipe.sectionTitles.tips}
              </h3>
              <ul className="tips-list">
                {recipe.tips.map((tip, index) => (
                  <li key={index} className="tip-item">
                    <span className="tip-icon">✨</span>
                    {tip}
                  </li>
                ))}
              </ul>
              <div className="serving-suggestion">
                <h4 className="serving-title">
                  <span className="serving-icon">🍽️</span>
                  {recipe.sectionTitles.serving}
                </h4>
                <p className="serving-text">{recipe.serving}</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <footer className="recipe-footer">
        <p>{recipe.footer}</p>
      </footer>
    </div>
  );
};

export default RecipeZone;
